import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentRestaurant } from "@/hooks/useCurrentRestaurant";
import { useDailyAnalytics, useAnalyticsSummary } from "@/query/useAnalytics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye, Users, Calendar, TrendingUp } from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

type DateRange = 7 | 30 | 90;

// Chart display configuration
const LONG_DATE_RANGE_THRESHOLD = 90;

// Helper function to format date as YYYY-MM-DD in local timezone
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { restaurantId } = useCurrentRestaurant();
  const [dateRange, setDateRange] = useState<DateRange>(7);

  // Calculate date range
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - dateRange);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { startDate: start, endDate: end };
  }, [dateRange]);

  // Fetch analytics data
  const { data: dailyData, isLoading: dailyLoading } = useDailyAnalytics(
    restaurantId,
    startDate,
    endDate
  );

  const { data: summary, isLoading: summaryLoading } = useAnalyticsSummary(
    restaurantId,
    startDate,
    endDate
  );

  // Format data for charts
  const chartData = useMemo(() => {
    if (!dailyData) return [];

    // Fill in missing dates with zero values
    const dataMap = new Map(dailyData.map((d) => [d.date, d]));
    const result = [];

    // Get today's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Start from startDate at midnight
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    // End date should be today (inclusive)
    const endDateOnly = new Date(today);
    endDateOnly.setHours(0, 0, 0, 0);

    // Iterate through dates including today
    while (currentDate <= endDateOnly) {
      const dateStr = formatDateLocal(currentDate);
      const data = dataMap.get(dateStr);

      result.push({
        date: dateStr,
        displayDate: currentDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        totalViews: data?.totalViews || 0,
        uniqueVisitors: data?.uniqueVisitors || 0,
      });

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return result;
  }, [dailyData, startDate]);

  const isLoading = dailyLoading || summaryLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div
        className="flex items-center justify-between"
        data-testid="analytics-header"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t("analytics.title", "Analytics")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t(
              "analytics.subtitle",
              "Track your menu views and visitor engagement"
            )}
          </p>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="flex gap-2">
        <Button
          data-testid="analytics-date-range-7"
          variant={dateRange === 7 ? "default" : "outline"}
          onClick={() => setDateRange(7)}
          size="sm"
        >
          <Calendar className="h-4 w-4 mr-2" />
          {t("analytics.last7Days", "Last 7 Days")}
        </Button>
        <Button
          data-testid="analytics-date-range-30"
          variant={dateRange === 30 ? "default" : "outline"}
          onClick={() => setDateRange(30)}
          size="sm"
        >
          <Calendar className="h-4 w-4 mr-2" />
          {t("analytics.last30Days", "Last 30 Days")}
        </Button>
        <Button
          data-testid="analytics-date-range-90"
          variant={dateRange === 90 ? "default" : "outline"}
          onClick={() => setDateRange(90)}
          size="sm"
        >
          <Calendar className="h-4 w-4 mr-2" />
          {t("analytics.last90Days", "Last 90 Days")}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-testid="analytics-total-views-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("analytics.totalViews", "Total Views")}
            </CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.totalViews?.toLocaleString() || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="inline h-3 w-3 mr-1" />
              {t("analytics.inSelectedPeriod", "In selected period")}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="analytics-unique-visitors-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("analytics.uniqueVisitors", "Unique Visitors")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.totalUniqueVisitors?.toLocaleString() || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="inline h-3 w-3 mr-1" />
              {t("analytics.inSelectedPeriod", "In selected period")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6">
        {/* Views Chart */}
        <Card data-testid="analytics-views-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              {t("analytics.dailyViews", "Daily Menu Views")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              data-testid="analytics-views-chart"
              className="h-[300px] w-full"
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="displayDate"
                    tick={{ fontSize: 12 }}
                    interval={
                      dateRange >= LONG_DATE_RANGE_THRESHOLD
                        ? "preserveStartEnd"
                        : 0
                    }
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="totalViews"
                    stroke="#7fb438"
                    strokeWidth={2}
                    name={t("analytics.views", "Views")}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Unique Visitors Chart */}
        <Card data-testid="analytics-visitors-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t("analytics.dailyUniqueVisitors", "Daily Unique Visitors")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              data-testid="analytics-visitors-chart"
              className="h-[300px] w-full"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="displayDate"
                    tick={{ fontSize: 12 }}
                    interval={
                      dateRange >= LONG_DATE_RANGE_THRESHOLD
                        ? "preserveStartEnd"
                        : 0
                    }
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="uniqueVisitors"
                    fill="#7fb438"
                    name={t("analytics.uniqueVisitors", "Unique Visitors")}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Message */}
      {chartData.length === 0 && (
        <Card data-testid="analytics-no-data">
          <CardContent className="py-12 text-center">
            <Eye className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">
              {t(
                "analytics.noData",
                "No data available for the selected period"
              )}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t(
                "analytics.shareMenu",
                "Share your menu to start collecting analytics"
              )}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
